/**
 * MASS EMAIL BROADCAST
 * 
 * Admin interface for sending mass email broadcasts to all members.
 * Features:
 * - WYSIWYG rich text editor
 * - Email preview with Aureus Alliance branding
 * - Test email functionality (sends to jp.rademeyer84@gmail.com)
 * - Two-step confirmation before mass send
 * - Progress tracking and audit logging
 * 
 * @version 1.0.0
 * @created 2025-10-23
 */

import React, { useState, useEffect } from 'react';
import { RichTextEditor } from './RichTextEditor';
import { getServerSessionId, getSessionHeaders } from '../../lib/serverSession';
import { generateMassEmailHTML, generateMassEmailText, validateEmailContent } from '../../lib/massEmailTemplate';
import { logAdminAction } from '../../lib/adminAuth';

interface User {
  id: number;
  email: string;
  username: string;
}

interface MassEmailBroadcastProps {
  currentUser: any;
}

const TEST_USER_IDS = [80, 4, 89, 102, 103, 104, 105, 106, 107, 111, 113, 115, 123];
const TEST_EMAIL = 'jp.rademeyer84@gmail.com';

export const MassEmailBroadcast: React.FC<MassEmailBroadcastProps> = ({ currentUser }) => {
  const [subject, setSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: number; failed: number } | null>(null);

  // Load recipient count on mount
  useEffect(() => {
    loadRecipientCount();
  }, []);

  const loadRecipientCount = async () => {
    try {
      console.log('[MassEmailBroadcast] Loading recipient count...');
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/mass-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'load_recipient_count', data: { testUserIds: TEST_USER_IDS } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      setRecipientCount(result.count);
      console.log(`[MassEmailBroadcast] Found ${result.count} valid recipients`);
    } catch (error) {
      console.error('[MassEmailBroadcast] Error loading recipient count:', error);
      setRecipientCount(0);
    }
  };

  const handlePreview = () => {
    const validation = validateEmailContent(subject, emailContent);
    if (!validation.valid) {
      alert('Please fix the following errors:\n\n' + validation.errors.join('\n'));
      return;
    }
    setShowPreview(true);
  };

  const handleSendTestEmail = async () => {
    const validation = validateEmailContent(subject, emailContent);
    if (!validation.valid) {
      alert('Please fix the following errors:\n\n' + validation.errors.join('\n'));
      return;
    }

    setLoading(true);
    try {
      console.log('[MassEmailBroadcast] Sending test email to:', TEST_EMAIL);

      const htmlEmail = generateMassEmailHTML({ subject, htmlContent: emailContent });
      const textEmail = generateMassEmailText({ subject, htmlContent: emailContent });

      // Call server-side API endpoint
      const response = await fetch('/api/send-mass-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          recipients: [TEST_EMAIL],
          subject,
          htmlContent: htmlEmail,
          textContent: textEmail,
          isTest: true
        })
      });

      const result = await response.json();

      if (result.success) {
        setTestEmailSent(true);
        alert(`✅ Test email sent successfully to ${TEST_EMAIL}!\n\nPlease check your inbox and verify the email looks correct before sending to all members.`);
        console.log('[MassEmailBroadcast] Test email sent successfully:', result);
      } else {
        throw new Error(result.error || 'Failed to send test email');
      }
    } catch (error: any) {
      console.error('[MassEmailBroadcast] Error sending test email:', error);
      alert(`❌ Failed to send test email: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToAll = () => {
    const validation = validateEmailContent(subject, emailContent);
    if (!validation.valid) {
      alert('Please fix the following errors:\n\n' + validation.errors.join('\n'));
      return;
    }

    if (!testEmailSent) {
      alert('⚠️ Please send a test email first to verify the content looks correct.');
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSend = async () => {
    if (!confirmationChecked) {
      alert('Please check the confirmation checkbox');
      return;
    }

    if (confirmationText !== 'SEND') {
      alert('Please type "SEND" to confirm');
      return;
    }

    setShowConfirmation(false);
    setIsSending(true);
    setSendResult(null);

    try {
      console.log('[MassEmailBroadcast] Starting mass email send...');

      // Get all valid recipients via API
      const sessionId = getServerSessionId();
      const recipientsResponse = await fetch('/api/admin/mass-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'get_recipients', data: { testUserIds: TEST_USER_IDS } })
      });
      const recipientsResult = await recipientsResponse.json();
      if (!recipientsResult.success) throw new Error(recipientsResult.error);

      const validUsers = recipientsResult.recipients;
      console.log(`[MassEmailBroadcast] Sending to ${validUsers.length} recipients...`);

      const recipients = validUsers.map((u: User) => u.email);
      const htmlEmail = generateMassEmailHTML({ subject, htmlContent: emailContent });
      const textEmail = generateMassEmailText({ subject, htmlContent: emailContent });

      // Update progress to show starting
      setSendingProgress({
        current: 0,
        total: recipients.length,
        percentage: 0
      });

      // Call server-side API endpoint for bulk send
      const response = await fetch('/api/send-mass-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          recipients,
          subject,
          htmlContent: htmlEmail,
          textContent: textEmail,
          isTest: false
        })
      });

      const result = await response.json();

      // Update progress to 100%
      setSendingProgress({
        current: recipients.length,
        total: recipients.length,
        percentage: 100
      });

      if (!result.success && result.failedBatches === result.results?.length) {
        throw new Error('All email batches failed to send');
      }

      // Calculate success/failure from batch results
      const successCount = result.results?.filter((r: any) => r.success).reduce((sum: number, r: any) => sum + r.recipients, 0) || 0;
      const failedCount = result.results?.filter((r: any) => !r.success).reduce((sum: number, r: any) => sum + r.recipients, 0) || 0;

      console.log(`[MassEmailBroadcast] Send complete: ${successCount} success, ${failedCount} failed`);

      // Log audit trail
      await logAdminAction(
        currentUser?.email || 'unknown',
        'MASS_EMAIL_BROADCAST',
        'mass_email',
        'all_users',
        {
          subject,
          total_recipients: recipients.length,
          successful_sends: successCount,
          failed_sends: failedCount,
          test_users_excluded: TEST_USER_IDS.length,
          timestamp: new Date().toISOString()
        }
      );

      setSendResult({ success: successCount, failed: failedCount });
      
      // Reset form
      setSubject('');
      setEmailContent('');
      setTestEmailSent(false);
      setConfirmationChecked(false);
      setConfirmationText('');

    } catch (error: any) {
      console.error('[MassEmailBroadcast] Error sending mass email:', error);
      alert(`❌ Failed to send mass email: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const previewHTML = subject && emailContent 
    ? generateMassEmailHTML({ subject, htmlContent: emailContent })
    : '';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">📧 Mass Email Broadcast</h2>
        <p className="text-white">
          Send email announcements to all registered members. Test emails are sent to {TEST_EMAIL}.
        </p>
      </div>

      {/* Recipient Count */}
      <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="text-3xl">👥</span>
          <div>
            <p className="text-white font-medium">
              This email will be sent to <span className="text-yellow-400 font-bold">{recipientCount.toLocaleString()}</span> members
            </p>
            <p className="text-white text-sm">
              (Excluding {TEST_USER_IDS.length} test users)
            </p>
          </div>
        </div>
      </div>

      {/* Subject Line */}
      <div className="mb-6">
        <label className="block text-white font-medium mb-2">
          📝 Subject Line <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Enter email subject line..."
          maxLength={200}
          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
        />
        <p className="text-white text-sm mt-1">
          {subject.length}/200 characters
        </p>
      </div>

      {/* Rich Text Editor */}
      <div className="mb-6">
        <label className="block text-white font-medium mb-2">
          ✍️ Email Content <span className="text-red-400">*</span>
        </label>
        <RichTextEditor
          value={emailContent}
          onChange={setEmailContent}
          placeholder="Compose your email message here..."
          maxLength={50000}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={handlePreview}
          disabled={!subject || !emailContent || loading}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          <span>👁️</span>
          Preview Email
        </button>

        <button
          onClick={handleSendTestEmail}
          disabled={!subject || !emailContent || loading}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          <span>🧪</span>
          {loading ? 'Sending...' : `Send Test Email to ${TEST_EMAIL}`}
        </button>

        <button
          onClick={handleSendToAll}
          disabled={!subject || !emailContent || !testEmailSent || loading || isSending}
          className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold rounded-lg transition-colors flex items-center gap-2"
        >
          <span>📧</span>
          Send to All {recipientCount.toLocaleString()} Members
        </button>
      </div>

      {/* Test Email Status */}
      {testEmailSent && (
        <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4 mb-6">
          <p className="text-green-400 flex items-center gap-2">
            <span>✅</span>
            Test email sent successfully! You can now send to all members.
          </p>
        </div>
      )}

      {/* Sending Progress */}
      {isSending && (
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-6 mb-6">
          <h3 className="text-white font-bold mb-4">📤 Sending Emails...</h3>
          <div className="mb-2">
            <div className="flex justify-between text-sm text-white mb-1">
              <span>{sendingProgress.current.toLocaleString()} of {sendingProgress.total.toLocaleString()} emails sent</span>
              <span>{sendingProgress.percentage}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-4">
              <div 
                className="bg-yellow-500 h-4 rounded-full transition-all duration-300"
                style={{ width: `${sendingProgress.percentage}%` }}
              />
            </div>
          </div>
          <p className="text-white text-sm mt-2">
            Please wait... This may take a few minutes.
          </p>
        </div>
      )}

      {/* Send Result */}
      {sendResult && (
        <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-6 mb-6">
          <h3 className="text-white font-bold mb-4">✅ Mass Email Sent Successfully!</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-white text-sm">Successful Sends</p>
              <p className="text-green-400 text-2xl font-bold">{sendResult.success.toLocaleString()}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-white text-sm">Failed Sends</p>
              <p className="text-red-400 text-2xl font-bold">{sendResult.failed.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">👁️ Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-white rounded-lg">
                <iframe
                  srcDoc={previewHTML}
                  className="w-full h-[600px] border-0"
                  title="Email Preview"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-700 flex justify-end gap-4">
              <button
                onClick={() => setShowPreview(false)}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold text-white">⚠️ Confirm Mass Email Send</h3>
            </div>
            <div className="p-6">
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 mb-6">
                <p className="text-yellow-400 font-medium mb-2">
                  ⚠️ WARNING: This action cannot be undone!
                </p>
                <p className="text-gray-300">
                  You are about to send this email to <span className="font-bold text-yellow-400">{recipientCount.toLocaleString()}</span> members.
                </p>
              </div>

              <div className="bg-gray-700 rounded-lg p-4 mb-6">
                <p className="text-white font-medium mb-2">📧 Email Details:</p>
                <p className="text-gray-300 text-sm mb-1">
                  <span className="font-medium">Subject:</span> {subject}
                </p>
                <p className="text-gray-300 text-sm">
                  <span className="font-medium">Recipients:</span> {recipientCount.toLocaleString()} members
                </p>
              </div>

              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmationChecked}
                    onChange={(e) => setConfirmationChecked(e.target.checked)}
                    className="mt-1 w-5 h-5"
                  />
                  <span className="text-white">
                    I confirm I want to send this email to all {recipientCount.toLocaleString()} members
                  </span>
                </label>
              </div>

              <div className="mb-6">
                <label className="block text-white font-medium mb-2">
                  Type <span className="text-yellow-400 font-bold">SEND</span> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type SEND here..."
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-700 flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  setConfirmationChecked(false);
                  setConfirmationText('');
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSend}
                disabled={!confirmationChecked || confirmationText !== 'SEND'}
                className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold rounded-lg"
              >
                Confirm Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

